import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.3 5.2C41.9 35.9 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.25h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z" />
    </svg>
  );
}

// Google (Emergent-managed) sign-in + Facebook Login.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function SocialAuth() {
  const nav = useNavigate();
  const { authenticate } = useAuth();
  const [fbReady, setFbReady] = useState(false);
  const [fbLoading, setFbLoading] = useState(false);

  const google = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!FB_APP_ID) return;
    window.fbAsyncInit = () => {
      window.FB.init({ appId: FB_APP_ID, cookie: true, xfbml: false, version: "v21.0" });
      setFbReady(true);
    };
    if (!document.getElementById("facebook-jssdk")) {
      const s = document.createElement("script");
      s.id = "facebook-jssdk";
      s.async = true; s.defer = true;
      s.src = "https://connect.facebook.net/en_US/sdk.js";
      document.body.appendChild(s);
    } else if (window.FB) setFbReady(true);
  }, []);

  const facebook = () => {
    setFbLoading(true);
    window.FB.login(async (result) => {
      if (!result.authResponse?.accessToken) { setFbLoading(false); return; }
      try {
        const { data } = await api.post("/auth/facebook", { access_token: result.authResponse.accessToken });
        authenticate(data.token, data.user);
        nav(data.user?.subscribed ? "/profiles" : "/subscribe");
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Facebook login failed");
      } finally { setFbLoading(false); }
    }, { scope: "public_profile,email" });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs text-zinc-500">
        <span className="h-px flex-1 bg-white/10" /> or continue with <span className="h-px flex-1 bg-white/10" />
      </div>
      <button type="button" onClick={google} data-testid="google-signin-btn"
        className="w-full flex items-center justify-center gap-2.5 bg-white text-black font-semibold py-3 rounded-lg hover:bg-zinc-200 transition-colors">
        <GoogleIcon /> Continue with Google
      </button>
      {FB_APP_ID && (
        <button type="button" onClick={facebook} disabled={!fbReady || fbLoading} data-testid="facebook-signin-btn"
          className="w-full flex items-center justify-center gap-2.5 bg-[#1877F2] text-white font-semibold py-3 rounded-lg hover:brightness-110 transition disabled:opacity-60">
          {fbLoading ? <Loader2 className="animate-spin" size={18} /> : <FacebookIcon />} Continue with Facebook
        </button>
      )}
    </div>
  );
}
